import { useState, useEffect } from "react";
import {
    Grid,
    Box,
    Tab,
    Tabs,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Snackbar,
    Alert,
    Divider
} from "@mui/material";
import { useTheme } from '@mui/material/styles';
import LoadingSwitch from "../../components/LoadingSwitch";
import MainCard from "../../components/cards/MainCard";
import {
    useUpdateTenancyMutation,
    useGetTenancyByIdQuery,
    useGetFeatureSwitchesQuery,
    useUpdateFeatureSwitchesMutation,
    tenancyApi
} from "../../store/services/tenancy.service";
import { useSelector, useDispatch } from "react-redux";
import { getCardColor } from '../../utils/colorUtils';

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`features-tabpanel-${index}`}
            aria-labelledby={`features-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box>
                    {children}
                </Box>
            )}
        </div>
    );
}

function a11yProps(index) {
    return {
        id: `features-tab-${index}`,
        'aria-controls': `features-tabpanel-${index}`,
    };
}

const FeaturesDefault = () => {
    const theme = useTheme();
    const lightMode = theme.palette.mode === 'light';

    const [tabValue, setTabValue] = useState(0);
    const [preset, setPreset] = useState('default');

    // Get tenancy ID from Redux store and setup dispatch
    const { tenancyId } = useSelector((state) => state.tenancy);
    const dispatch = useDispatch();

    // Get tenancy data from API
    const {
        data: tenancyData,
        isLoading: tenancyLoading,
        error: tenancyError
    } = useGetTenancyByIdQuery({ tenancyId });

    // Get feature switches data from API
    const {
        data: featureSwitchesApiData,
        isLoading: featureSwitchesLoading,
        error: featureSwitchesError
    } = useGetFeatureSwitchesQuery({ tenancyId });

    // Log feature switches data when it changes
    useEffect(() => {
        if (featureSwitchesApiData) {

            // Check if we need to access the nested data property
            const actualData = featureSwitchesApiData.data || featureSwitchesApiData;
        }
    }, [featureSwitchesApiData]);

    // API mutations
    const [updateTenancy] = useUpdateTenancyMutation();
    const [updateFeatureSwitches] = useUpdateFeatureSwitchesMutation();

    // Snackbar state
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    // Loading states
    const [loadingStates, setLoadingStates] = useState({
        enableNewsFeed: false,
        enableSettings: false,
        enableContent: false,
        enableReporting: false,
        enableTenants: false,
        enableHelp: false,
        enableTrillion: false,
        showTrillion: false,
        enableDashboard: false,
        enableDashboardCourseOverview: false,
        enableDashboardActivityTimeline: false,
        enableDashboardUserSearch: false,
        enableDashboardMostClickedSimulations: false,
        enableDashboardDepartmentOverview: false,
        enableOndemand: false,
        enableSimulation: false,
        enableAddSimulation: false,
        enableCourse: false,
        enableJourneyBuilder: false,
        enableContentPacks: false,
        enableSettingsGeneral: false,
        enableSettingsDeliverability: false,
        enableSettingsBranding: false,
        enableSettingsCompanyInfo: false,
        enableSettingsPhishReportButton: false,
        enableNotifications: false,
        enableMfa: false,
        enableCustomEmailHeaders: false,
        enableCustomEmailSubdomain: false,
        enableReminders: false,
        enableLineManagerEscalation: false,
        enablePauseJourneys: false,
        enableSendingInfo: false,
        enableMicrosoftIntegration: false,
        enableGoogleIntegration: false,
        enableGateways: false,
        enableBrandingTheme: false,
        enableBrandingEmails: false,
        enableBrandingLandingPage: false,
        enableReportingApi: false,
        enableRandomSimulation: false,
        enableJourneyBuilderSimulations: false,
        enableJourneyBuilderCourses: false,
        enableBrandingReportedSimEmails: false,
        enableAddCourse: false,
        enableTeamsIntegration: false,
        enableTeamsUI: false,
        enableEmailUI: false,
        enableDmi: false
    });

    // Navigation Bar Items - Main Tabs
    const NAV_BAR_ITEMS = [
        { key: 'enableNewsFeed', name: 'News Feed', tooltip: 'Enable or disable News Feed navigation item' },
        { key: 'enableSettings', name: 'Settings', tooltip: 'Enable or disable Settings navigation item' },
        { key: 'enableContent', name: 'Content', tooltip: 'Enable or disable Content navigation item' },
        { key: 'enableReporting', name: 'Reports', tooltip: 'Enable or disable Reports navigation item' },
        { key: 'enableTenants', name: 'Tenants', tooltip: 'Enable or disable Tenants navigation item' },
        { key: 'enableHelp', name: 'Help', tooltip: 'Enable or disable Help navigation item' },
        { key: 'enableDashboard', name: 'Dashboard', tooltip: 'Enable or disable Dashboard navigation item' },
        { key: 'showTrillion', name: 'DWM', tooltip: 'Show/hide Dark Web Monitoring navigation item' }
    ];

    // Feature configuration objects organized by nav bar hierarchy
    const FEATURE_CONFIG = {
        newsFeed: {
            master: 'enableNewsFeed',
            features: []
        },
        settings: {
            master: 'enableSettings',
            secondaryTabs: [
                {
                    name: 'General',
                    master: 'enableSettingsGeneral',
                    features: [
                        { key: 'enableCustomEmailHeaders', name: 'Custom Email Headers', tooltip: 'Enable or disable custom email headers' },
                        { key: 'enableCustomEmailSubdomain', name: 'Custom Email Domain', tooltip: 'Enable or disable custom email domain for sending courses' },
                        { key: 'enableReminders', name: 'Reminders', tooltip: 'Enable or disable email reminders' },
                        { key: 'enableLineManagerEscalation', name: 'Line Manager Escalation', tooltip: 'Enable or disable line manager escalation for reminders' },
                        { key: 'enablePauseJourneys', name: 'Pause Emails', tooltip: 'Enable or disable emails for the tenancy' },
                        { key: 'enableNotifications', name: 'Course Notifications', tooltip: 'Enable or disable course notifications section in settings' },
                        { key: 'enableTeamsUI', name: 'Teams Notification UI', tooltip: 'Enable or disable Teams notification toggle in course notifications', parentKey: 'enableNotifications' },
                        { key: 'enableEmailUI', name: 'Email Notification UI', tooltip: 'Enable or disable Email notification toggle in course notifications', parentKey: 'enableNotifications' }
                    ]
                },
                {
                    name: 'Deliverability',
                    master: 'enableSettingsDeliverability',
                    features: [
                        { key: 'enableSendingInfo', name: 'Sending Information', tooltip: 'Enable or disable Sending Information tab' },
                        { key: 'enableMicrosoftIntegration', name: 'Microsoft Integration', tooltip: 'Enable or disable Microsoft/Office 365 integration' },
                        { key: 'enableGoogleIntegration', name: 'Google Integration', tooltip: 'Enable or disable Google Workspace integration' },
                        { key: 'enableGateways', name: 'Gateways', tooltip: 'Enable or disable gateway configurations' }
                    ]
                },
                {
                    name: 'Branding',
                    master: 'enableSettingsBranding',
                    features: [
                        { key: 'enableBrandingTheme', name: 'Theme Customisation', tooltip: 'Enable or disable theme customization features' },
                        { key: 'enableBrandingEmails', name: 'Custom Email Templates', tooltip: 'Enable or disable email template branding' },
                        { key: 'enableBrandingReportedSimEmails', name: 'Custom PRI Emails', tooltip: 'Enable or disable custom PRI email branding' },
                        { key: 'enableBrandingLandingPage', name: 'Custom Landing Pages', tooltip: 'Enable or disable landing page branding' },
                    { key: 'enableDmi', name: 'Direct Mail Injection', tooltip: 'Enable simulation email injection via Azure Function App and Microsoft Graph API instead of SMTP' }
                    ]
                },
                {
                    name: 'Company Information',
                    master: 'enableSettingsCompanyInfo',
                    features: []
                },
                {
                    name: 'Phish Report Button',
                    master: 'enableSettingsPhishReportButton',
                    features: []
                },
                {
                    name: 'API Integration',
                    features: [
                        { key: 'enableReportingApi', name: 'Reporting API', tooltip: 'Enable or disable Reporting API integration' }
                    ]
                },
                {
                    name: 'Teams Integration',
                    master: 'enableTeamsIntegration',
                    features: []
                }
            ]
        },
        content: {
            master: 'enableContent',
            secondaryTabs: [
                {
                    name: 'On Demand',
                    master: 'enableOndemand',
                    features: [
                        { key: 'enableSimulation', name: 'On Demand Simulations', tooltip: 'Enable or disable simulation features in on-demand' },
                        { key: 'enableAddSimulation', name: 'On Demand Add Simulations', tooltip: 'Enable or disable the ability to create new simulations' },
                        { key: 'enableCourse', name: 'On Demand Courses', tooltip: 'Enable or disable course features in on-demand' }
                    ]
                },
                {
                    name: 'Journey Builder',
                    master: 'enableJourneyBuilder',
                    features: [
                        { key: 'enableJourneyBuilderSimulations', name: 'Journey Builder Simulations', tooltip: 'Enable or disable simulations in Journey Builder' },
                        { key: 'enableJourneyBuilderCourses', name: 'Journey Builder Courses', tooltip: 'Enable or disable courses in Journey Builder' }
                    ]
                },
                {
                    name: 'Content Packs',
                    master: 'enableContentPacks',
                    features: []
                },
                {
                    name: 'Random Simulations',
                    master: 'enableRandomSimulation',
                    features: []
                },
                {
                    name: 'Customer Courses',
                    master: 'enableAddCourse',
                    features: []
                }

            ]
        },
        reports: {
            master: 'enableReporting',
            features: []
        },
        tenants: {
            master: 'enableTenants',
            features: []
        },
        help: {
            master: 'enableHelp',
            features: []
        },
        dashboard: {
            master: 'enableDashboard',
            secondaryTabs: [
                {
                    name: 'Dashboard',
                    master: 'enableDashboard',
                    features: [
                        { key: 'enableDashboardCourseOverview', name: 'Your Courses', tooltip: 'Enable or disable Your Courses section on Dashboard' },
                        { key: 'enableDashboardActivityTimeline', name: 'Activity Timeline', tooltip: 'Enable or disable Activity Timeline section on Dashboard' },
                        { key: 'enableDashboardUserSearch', name: 'User Search & Analytics', tooltip: 'Enable or disable User Search and Analytics section on Dashboard' },
                        { key: 'enableDashboardMostClickedSimulations', name: 'Most Clicked Simulations', tooltip: 'Enable or disable Simulation Performance section on Dashboard' },
                        { key: 'enableDashboardDepartmentOverview', name: 'Department Overview', tooltip: 'Enable or disable Department Overview section on Dashboard' }
                    ]
                }
            ]
        },
        trillion: {
            master: 'showTrillion',
             secondaryTabs: [
                {
                    name: 'Show/hide Dark Web Monitoring button',
                    master: 'showTrillion',
                    features: [
                        { key: 'enableTrillion', name: 'Enable/disable Dark Web Monitoring Button', tooltip: 'Controls wether the Trillion button is greyed out' },
                    ]
                }
            ]
        },
    };

    // Special features that don't belong to nav bar items
    const SPECIAL_FEATURES = [
        { key: 'enableCourseRetake', name: 'Enable Course Retake', tooltip: 'Enable users to retake courses', section: 'Course Settings' }
    ];

    // Define theme-aware table styles
    const getTableStyles = () => ({
        tableContainer: {
            backgroundColor: lightMode ? 'inherit' : getCardColor(lightMode),
            borderRadius: theme.borderRadius?.sm || 1,
            border: lightMode ? 'none' : `1px solid ${theme.palette.grey[600]}`,
        },
        table: {
            backgroundColor: 'transparent', // Let container handle background
        },
        tableHead: {
            backgroundColor: lightMode
                ? theme.palette.grey[50]
                : theme.palette.grey[800],
        },
        tableHeaderCell: {
            backgroundColor: 'inherit',
            color: lightMode
                ? theme.palette.text.primary
                : theme.palette.text.primary,
            fontWeight: 600,
            borderBottom: `1px solid ${lightMode
                ? theme.palette.grey[200]
                : theme.palette.grey[700]}`,
        },
        tableBodyCell: {
            borderBottom: `1px solid ${lightMode
                ? theme.palette.grey[200]
                : theme.palette.grey[700]}`,
            color: theme.palette.text.primary,
        },
        tableRow: {
            '&:hover': {
                backgroundColor: lightMode
                    ? theme.palette.action.hover
                    : theme.palette.grey[800],
            },
            '&:last-child td': {
                borderBottom: 'none',
            },
        }
    });

    const tableStyles = getTableStyles();

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        // Reset secondary tab when changing main tabs
        setSecondaryTabValues({
            settings: 0,
            content: 0,
            dashboard: 0
        });
    };

    const handlePresetChange = (event) => {
        setPreset(event.target.value);
    };

    // Secondary tab states
    const [secondaryTabValues, setSecondaryTabValues] = useState({
        settings: 0,
        content: 0,
        dashboard: 0
    });

    const handleSecondaryTabChange = (section, newValue) => {
        setSecondaryTabValues(prev => ({
            ...prev,
            [section]: newValue
        }));
    };

    const setLoadingFor = (setting, isLoading) => {
        setLoadingStates(prev => ({
            ...prev,
            [setting]: isLoading
        }));
    };

    const isAnySwitchLoading = () => {
        return Object.values(loadingStates).some(state => state === true);
    };

    // Get current master switch based on active tab
    const getCurrentMasterSwitch = () => {
        const tabKeys = ['newsFeed', 'settings', 'content', 'reports', 'tenants', 'help', 'dashboard', 'trillion'];
        const currentTabKey = tabKeys[tabValue];
        return FEATURE_CONFIG[currentTabKey]?.master;
    };

    // Get current master switch value
    const getCurrentMasterSwitchValue = () => {
        const masterKey = getCurrentMasterSwitch();
        if (!masterKey || !featureSwitchesApiData) return false;

        const actualData = featureSwitchesApiData?.data || featureSwitchesApiData;
        return actualData[masterKey] || false;
    };

    // Handle master switch change
    const handleMasterSwitchChange = async () => {
        const masterKey = getCurrentMasterSwitch();
        if (!masterKey || !featureSwitchesApiData) return;

        const actualData = featureSwitchesApiData?.data || featureSwitchesApiData;
        const currentValue = actualData[masterKey];
        const newValue = !currentValue;

        setLoadingFor(masterKey, true);

        try {
            await updateFeatureSwitches({
                tenancyId: tenancyId,
                data: {
                    [masterKey]: newValue
                }
            }).unwrap();

            const featureName = NAV_BAR_ITEMS.find(item => item.key === masterKey)?.name || 'Feature';
            setSnackbarMessage(`${featureName} setting updated successfully.`);
            setSnackbarSeverity("success");

            // For parent tenancies, always force a refresh
            if (isParentTenancy()) {
                console.log("Parent toggle changed, forcing refresh");
                dispatch(tenancyApi.util.invalidateTags([
                    { type: "FeatureSwitches", id: "LIST" },
                    { type: "Tenancy", id: "LIST" }
                ]));
            }
        } catch (error) {
            console.error(`Error updating ${masterKey} setting:`, error);

            const featureName = NAV_BAR_ITEMS.find(item => item.key === masterKey)?.name || 'Feature';

            if (error.data && error.data.message) {
                setSnackbarMessage(error.data.message);
            } else {
                setSnackbarMessage(`Error updating ${featureName} setting.`);
            }
            setSnackbarSeverity("error");
        } finally {
            setSnackbarOpen(true);
            setLoadingFor(masterKey, false);
        }
    };

    // Updated FeatureSwitch component with theme-aware styling
    const FeatureSwitch = ({ feature, checked, onToggle, loading, disabled }) => (
        <TableRow
            key={`${feature.key}-row`}
            sx={tableStyles.tableRow}
        >
            <TableCell
                component="th"
                scope="row"
                sx={tableStyles.tableBodyCell}
            >
                <Typography variant="body2">
                    {feature.name}
                </Typography>
            </TableCell>
            <TableCell sx={tableStyles.tableBodyCell}>
                <Box display="flex" alignItems="center">
                    <LoadingSwitch
                        checked={checked}
                        onChange={onToggle}
                        loading={loading}
                        disabled={disabled || isAnySwitchLoading()}
                        inputProps={{ "aria-label": "controlled" }}
                    />
                    {loading && (
                        <Typography variant="caption" color="info.main" sx={{ ml: 1 }}>
                            Updating...
                        </Typography>
                    )}
                </Box>
            </TableCell>
        </TableRow>
    );

    // Determine if this is a parent tenancy (no parentSettings property)
    const isParentTenancy = () => {
        const actualData = featureSwitchesApiData?.data || featureSwitchesApiData;
        const isParent = actualData && actualData.parentSettings === null;

        if (isParent) {
            console.log("Parent tenancy detected:", tenancyId);
            console.log("Using data:", actualData);
        }

        return isParent;
    };

    // Generic function to render feature rows
    const renderFeatureRows = (features) => {
        const rows = [];
        const actualData = featureSwitchesApiData?.data || featureSwitchesApiData;

        features.forEach(feature => {
            rows.push(
                <FeatureSwitch
                    key={feature.key}
                    feature={feature}
                    checked={actualData?.[feature.key] ?? false}
                    onToggle={() => handleGenericFeatureToggle(feature.key)}
                    loading={loadingStates[feature.key]}
                />
            );
        });

        return rows;
    };

    // Render master switch row for secondary tabs
    const renderMasterSwitchRow = (masterKey, name) => {
        if (!masterKey) return null;
        const actualData = featureSwitchesApiData?.data || featureSwitchesApiData;

        return (
            <FeatureSwitch
                key={masterKey}
                feature={{ key: masterKey, name: name }}
                checked={actualData?.[masterKey] ?? false}
                onToggle={() => handleGenericFeatureToggle(masterKey)}
                loading={loadingStates[masterKey]}
            />
        );
    };

    // Updated render function for tables with theme styling
    const renderThemedTable = (content, ariaLabel) => (
        <TableContainer sx={tableStyles.tableContainer}>
            <Table
                sx={{
                    ...tableStyles.table,
                    minWidth: 650,
                    tableLayout: 'fixed'
                }}
                aria-label={ariaLabel}
            >
                <TableHead sx={tableStyles.tableHead}>
                    <TableRow>
                        <TableCell
                            width="75%"
                            sx={tableStyles.tableHeaderCell}
                        >
                            Feature
                        </TableCell>
                        <TableCell
                            width="25%"
                            sx={tableStyles.tableHeaderCell}
                        >
                            On/Off
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {content}
                </TableBody>
            </Table>
        </TableContainer>
    );

    // Helper function to find child features that depend on a parent key
    const getChildFeatures = (parentKey) => {
        const children = [];
        for (const category of Object.values(FEATURE_CONFIG)) {
            if (category.features) {
                category.features.forEach(f => {
                    if (f.parentKey === parentKey) children.push(f.key);
                });
            }
            if (category.secondaryTabs) {
                for (const tab of category.secondaryTabs) {
                    tab.features.forEach(f => {
                        if (f.parentKey === parentKey) children.push(f.key);
                    });
                }
            }
        }
        return children;
    };

    // Generic feature toggle handler
    const handleGenericFeatureToggle = async (featureKey) => {
        if (!featureSwitchesApiData) return;

        const actualData = featureSwitchesApiData?.data || featureSwitchesApiData;
        const currentValue = actualData[featureKey];
        const newValue = !currentValue;

        setLoadingFor(featureKey, true);

        // Build the update data - if turning off a parent, also turn off its children
        const updateData = { [featureKey]: newValue };
        if (!newValue) {
            const childFeatures = getChildFeatures(featureKey);
            childFeatures.forEach(childKey => {
                updateData[childKey] = false;
            });
        }

        try {
            await updateFeatureSwitches({
                tenancyId: tenancyId,
                data: updateData
            }).unwrap();

            // Get feature name for display
            const getFeatureName = (key) => {
                // Check nav bar items
                const navItem = NAV_BAR_ITEMS.find(item => item.key === key);
                if (navItem) return navItem.name;

                // Check feature config
                for (const category of Object.values(FEATURE_CONFIG)) {
                    if (category.features) {
                        const feature = category.features.find(f => f.key === key);
                        if (feature) return feature.name;
                    }
                    if (category.secondaryTabs) {
                        for (const tab of category.secondaryTabs) {
                            if (tab.master === key) return tab.name;
                            const feature = tab.features.find(f => f.key === key);
                            if (feature) return feature.name;
                        }
                    }
                }

                return key;
            };

            const featureName = getFeatureName(featureKey);
            setSnackbarMessage(`${featureName} setting updated successfully.`);
            setSnackbarSeverity("success");

            // For parent tenancies, always force a refresh to ensure UI stays in sync with backend
            if (isParentTenancy()) {
                console.log("Parent toggle changed, forcing refresh");
                dispatch(tenancyApi.util.invalidateTags([
                    { type: "FeatureSwitches", id: "LIST" },
                    { type: "Tenancy", id: "LIST" }
                ]));
            }
        } catch (error) {
            console.error(`Error updating ${featureKey} setting:`, error);

            const getFeatureName = (key) => {
                // Check nav bar items
                const navItem = NAV_BAR_ITEMS.find(item => item.key === key);
                if (navItem) return navItem.name;

                // Check feature config
                for (const category of Object.values(FEATURE_CONFIG)) {
                    if (category.features) {
                        const feature = category.features.find(f => f.key === key);
                        if (feature) return feature.name;
                    }
                    if (category.secondaryTabs) {
                        for (const tab of category.secondaryTabs) {
                            if (tab.master === key) return tab.name;
                            const feature = tab.features.find(f => f.key === key);
                            if (feature) return feature.name;
                        }
                    }
                }

                return key;
            };

            const featureName = getFeatureName(featureKey);

            if (error.data && error.data.message) {
                setSnackbarMessage(error.data.message);
            } else {
                setSnackbarMessage(`Error updating ${featureName} setting.`);
            }
            setSnackbarSeverity("error");
        } finally {
            setSnackbarOpen(true);
            setLoadingFor(featureKey, false);
        }
    };

    return (
        <Grid container rowSpacing={4.5} columnSpacing={2.75}>
            <Grid item xs={12} md={12} lg={12}>
                <>
                    <Box display="flex" justifyContent="space-between" sx={{ mb: 2 }} alignItems="flex-end">
                        <Tabs
                            value={tabValue}
                            onChange={handleTabChange}
                            aria-label="nav bar features management tabs"
                        >
                            {NAV_BAR_ITEMS.map((item, index) => (
                                <Tab key={item.key} label={item.name} {...a11yProps(index)} />
                            ))}
                        </Tabs>
                    </Box>

                    <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 1.5,
                        pb: 1,
                        width: "300px",
                        borderRadius: 1
                    }}>
                        <Typography variant="h5" sx={{ mr: 2 }}>
                            Master Switch
                        </Typography>
                        <LoadingSwitch
                            checked={getCurrentMasterSwitchValue()}
                            onChange={handleMasterSwitchChange}
                            loading={loadingStates[getCurrentMasterSwitch()]}
                            inputProps={{ "aria-label": "controlled" }}
                        />
                    </Box>

                    {/* News Feed Tab */}
                    <TabPanel value={tabValue} index={0}>
                        <MainCard content={true} boxShadow={true} border={false} sx={{ bgcolor: "background.default" }} >
                            <Typography variant="h6" sx={{ p: 2 }}>
                                News Feed feature has no additional sub-features to configure.
                            </Typography>
                        </MainCard>
                    </TabPanel>

                    {/* Settings Tab */}
                    <TabPanel value={tabValue} index={1}>
                        <MainCard content={true} boxShadow={true} border={false} sx={{ bgcolor: "background.default" }} >
                            <Box sx={{ px: 1, pt: 0.5 }}>
                                <Tabs
                                    value={secondaryTabValues.settings}
                                    onChange={(e, v) => handleSecondaryTabChange('settings', v)}
                                    aria-label="settings secondary tabs"
                                    variant="scrollable"
                                    scrollButtons="auto"
                                >
                                    {FEATURE_CONFIG.settings.secondaryTabs.map((tab, index) => (
                                        <Tab key={tab.name} label={tab.name} />
                                    ))}
                                </Tabs>
                                <Divider sx={{ mt: 0.5, mb: 1 }} />
                            </Box>

                            <Box>
                                {FEATURE_CONFIG.settings.secondaryTabs.map((tab, index) => (
                                    secondaryTabValues.settings === index && (
                                        <div key={tab.name}>
                                            {tenancyLoading ? (
                                                <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                                                    <CircularProgress />
                                                </Box>
                                            ) : tenancyError ? (
                                                <Box sx={{ p: 3 }}>
                                                    <Typography color="error">Error loading tenancy data</Typography>
                                                </Box>
                                            ) : (
                                                renderThemedTable(
                                                    <>
                                                        {renderMasterSwitchRow(tab.master, tab.name)}
                                                        {renderFeatureRows(tab.features)}
                                                    </>,
                                                    `${tab.name} features table`
                                                )
                                            )}
                                        </div>
                                    )
                                ))}
                            </Box>
                        </MainCard>
                    </TabPanel>

                    {/* Content Tab */}
                    <TabPanel value={tabValue} index={2}>
                        <MainCard content={true} boxShadow={true} border={false} sx={{ bgcolor: "background.default" }} >
                            <Box sx={{ px: 1, pt: 0.5 }}>
                                <Tabs
                                    value={secondaryTabValues.content}
                                    onChange={(e, v) => handleSecondaryTabChange('content', v)}
                                    aria-label="content secondary tabs"
                                >
                                    {FEATURE_CONFIG.content.secondaryTabs.map((tab, index) => (
                                        <Tab key={tab.name} label={tab.name} />
                                    ))}
                                </Tabs>
                                <Divider sx={{ mt: 0.5, mb: 1 }} />
                            </Box>

                            <Box>
                                {FEATURE_CONFIG.content.secondaryTabs.map((tab, index) => (
                                    secondaryTabValues.content === index && (
                                        <div key={tab.name}>
                                            {tenancyLoading ? (
                                                <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                                                    <CircularProgress />
                                                </Box>
                                            ) : tenancyError ? (
                                                <Box sx={{ p: 3 }}>
                                                    <Typography color="error">Error loading tenancy data</Typography>
                                                </Box>
                                            ) : (
                                                renderThemedTable(
                                                    <>
                                                        {renderMasterSwitchRow(tab.master, tab.name)}
                                                        {renderFeatureRows(tab.features)}
                                                    </>,
                                                    `${tab.name} features table`
                                                )
                                            )}
                                        </div>
                                    )
                                ))}
                            </Box>
                        </MainCard>
                    </TabPanel>

                    {/* Reports Tab */}
                    <TabPanel value={tabValue} index={3}>
                        <MainCard content={true} boxShadow={true} border={false} sx={{ bgcolor: "background.default" }} >
                            <Typography variant="h6" sx={{ p: 2 }}>
                                Reports feature has no additional sub-features to configure.
                            </Typography>
                        </MainCard>
                    </TabPanel>

                    {/* Tenants Tab */}
                    <TabPanel value={tabValue} index={4}>
                        <MainCard content={true} boxShadow={true} border={false} sx={{ bgcolor: "background.default" }} >
                            <Typography variant="h6" sx={{ p: 2 }}>
                                Tenants feature has no additional sub-features to configure.
                            </Typography>
                        </MainCard>
                    </TabPanel>

                    {/* Help Tab */}
                    <TabPanel value={tabValue} index={5}>
                        <MainCard content={true} boxShadow={true} border={false} sx={{ bgcolor: "background.default" }} >
                            <Typography variant="h6" sx={{ p: 2 }}>
                                Help feature has no additional sub-features to configure.
                            </Typography>
                        </MainCard>
                    </TabPanel>

                    {/* Dashboard Tab */}
                    <TabPanel value={tabValue} index={6}>
                        <MainCard content={true} boxShadow={true} border={false} sx={{ bgcolor: "background.default" }} >
                            <Box sx={{ px: 1, pt: 0.5 }}>
                                <Tabs
                                    value={secondaryTabValues.dashboard}
                                    onChange={(e, v) => handleSecondaryTabChange('dashboard', v)}
                                    aria-label="dashboard secondary tabs"
                                >
                                    {FEATURE_CONFIG.dashboard.secondaryTabs.map((tab, index) => (
                                        <Tab key={tab.name} label={tab.name} />
                                    ))}
                                </Tabs>
                                <Divider sx={{ mt: 0.5, mb: 1 }} />
                            </Box>

                            <Box>
                                {FEATURE_CONFIG.dashboard.secondaryTabs.map((tab, index) => (
                                    secondaryTabValues.dashboard === index && (
                                        <div key={tab.name}>
                                            {tenancyLoading ? (
                                                <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                                                    <CircularProgress />
                                                </Box>
                                            ) : tenancyError ? (
                                                <Box sx={{ p: 3 }}>
                                                    <Typography color="error">Error loading tenancy data</Typography>
                                                </Box>
                                            ) : (
                                                renderThemedTable(
                                                    <>
                                                        {renderMasterSwitchRow(tab.master, tab.name)}
                                                        {renderFeatureRows(tab.features)}
                                                    </>,
                                                    `${tab.name} features table`
                                                )
                                            )}
                                        </div>
                                    )
                                ))}
                            </Box>
                        </MainCard>
                    </TabPanel>

                    {/* DWM Tab */}
                    <TabPanel value={tabValue} index={7}>
                        <MainCard content={true} boxShadow={true} border={false} sx={{ bgcolor: "background.default" }} >
                            {FEATURE_CONFIG.trillion.secondaryTabs.map((tab, index) => (
                                <div key={tab.name}>
                                    {tenancyLoading ? (
                                        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                                            <CircularProgress />
                                        </Box>
                                    ) : tenancyError ? (
                                        <Box sx={{ p: 3 }}>
                                            <Typography color="error">Error loading tenancy data</Typography>
                                        </Box>
                                    ) : (
                                        renderThemedTable(
                                            <>
                                                {renderMasterSwitchRow(tab.master, tab.name)}
                                                {renderFeatureRows(tab.features)}
                                            </>,
                                            `${tab.name} features table`
                                        )
                                    )}
                                </div>
                            ))}
                        </MainCard>
                    </TabPanel>
                </>
            </Grid>

            <Snackbar
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}>
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} variant="filled"
                    sx={{ width: "100%" }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Grid>
    );
};

export default FeaturesDefault;